import { Link } from "react-router-dom";
import type { Article } from "@/mocks/news";
import {
  categoryAccent,
  formatDate,
  formatViews,
  getCategoryName,
} from "@/utils/news";

interface ArticleCardProps {
  article: Article;
  layout?: "row" | "column";
}

export default function ArticleCard({
  article,
  layout = "row",
}: ArticleCardProps) {
  const isRow = layout === "row";

  return (
    <article
      className={`group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors ${
        isRow
          ? "flex flex-col sm:flex-row gap-4 p-4"
          : "flex flex-col p-4"
      }`}
    >
      <Link
        to={`/detail/${article.id}`}
        className={`block shrink-0 overflow-hidden rounded-md cursor-pointer ${
          isRow ? "w-full sm:w-40 md:w-48" : "w-full"
        }`}
      >
        <div
          className={`w-full overflow-hidden rounded-md ${
            isRow ? "h-40 sm:h-28 md:h-32" : "h-44"
          }`}
        >
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 51爆料`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${categoryAccent(
              article.category,
            )}`}
          >
            {getCategoryName(article.category)}
          </span>
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            {article.subCategory}
          </span>
          {article.hot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500 text-background-50 whitespace-nowrap flex items-center gap-0.5">
              <i className="ri-fire-line" />
              热爆
            </span>
          )}
        </div>

        <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 leading-snug">
          <Link
            to={`/detail/${article.id}`}
            className="hover:text-primary-600 transition-colors cursor-pointer"
          >
            {article.title}
          </Link>
        </h3>

        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.summary}
        </p>

        <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
          <span className="whitespace-nowrap">{formatDate(article.date)}</span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </article>
  );
}